export type SearchCallback = (search: string) => void;

class SearchService {
  private static instance: SearchService;
  private subscribers: SearchCallback[] = [];

  private search: string = '';

  private constructor() { }

  public static getInstance(): SearchService {
    if (!SearchService.instance) {
      SearchService.instance = new SearchService();
    }
    return SearchService.instance;
  }

  public getSearch(): string {
    return this.search;
  }

  public setSearch(search: string): void {
    this.search = search;
    this.notifySubscribers();
  }

  /**
   * Subscribe to changes on the search field.
   * Notifies immediately with the current search field.
   * @param callback A function that will be called whenever the search changes.
   * @param options An optional object containing an AbortSignal.
   * @returns An unsubscribe function.
   */
  public subscribe(callback: SearchCallback, options?: { signal?: AbortSignal }): () => void {
    this.subscribers.push(callback);
    callback(this.search);

    let abortHandler: () => void;

    const unsubscribe = () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
      if (options?.signal && abortHandler) {
        options.signal.removeEventListener('abort', abortHandler);
      }
    };

    if (options?.signal) {
      // If already aborted, unsubscribe immediately.
      if (options.signal.aborted) {
        unsubscribe();
      } else {
        abortHandler = () => {
          unsubscribe();
        };
        options.signal.addEventListener('abort', abortHandler);
      }
    }
    return unsubscribe;
  }

  /**
   * Calls all subscriber callbacks with the updated search.
   */
  private notifySubscribers(): void {
    this.subscribers.forEach(callback => callback(this.search));
  }
}

export const searchService = SearchService.getInstance();
